import { Injectable } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { NumberOfEmployeesRelativeYears } from '@models/filterEnums.model';
import { LoggingService } from '@services/logging.service';
import { LookupService } from './lookup.service';
import { Lookup } from '@models/lookup.model';
import _ from 'lodash-es';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LookupType } from '../mystudies/_study_constants';

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  constructor(private logger: LoggingService, private lookupService: LookupService) {}

  public async getCompanyCategoriesBySize(readOnly: boolean): Promise<TreeNode[]> {
    this.logger.info(this.constructor.name, 'getCompanyCategoriesBySize', `Start`);
    return firstValueFrom(this.getCompanyCategoriesBySize$(readOnly));
  }

  public getCompanyCategoriesBySize$(readOnly: boolean): Observable<TreeNode[]> {
    return this.lookupService
      .GetCompanySize$()
      .pipe(map((companySizes) => this.convertDataToTreeNode(companySizes, this.getParentId(companySizes), readOnly))) as Observable<TreeNode[]>;
  }

  public getListingStatuses$(readOnly: boolean): Observable<TreeNode[]> {
    return this.lookupService
      .getListingStatuses$()
      .pipe(map((listingStatuses) => this.convertDataToTreeNode(listingStatuses, this.getParentId(listingStatuses), readOnly))) as Observable<
      TreeNode[]
    >;
  }

  public async getStatuses(readOnly: boolean): Promise<TreeNode[]> {
    this.logger.info(this.constructor.name, 'getStatuses', `Start`);
    return firstValueFrom(this.getStatuses$(readOnly));
  }

  public getStatuses$(readOnly: boolean): Observable<TreeNode[]> {
    return this.lookupService
      .GetCompanyStatus$()
      .pipe(map((statuses) => this.convertDataToTreeNode(statuses, this.getParentId(statuses), readOnly))) as Observable<TreeNode[]>;
  }

  public async getLegalForms(readOnly: boolean): Promise<TreeNode[]> {
    this.logger.info(this.constructor.name, 'getLegalForms', `Start`);
    return firstValueFrom(this.getLegalForms$(readOnly));
  }

  public getLegalForms$(readOnly: boolean): Observable<TreeNode[]> {
    return this.lookupService
      .GetLegalForm$()
      .pipe(map((legalForms) => this.convertDataToTreeNode(legalForms, this.getParentId(legalForms), readOnly))) as Observable<TreeNode[]>;
  }

  public async getAccountTypes(readOnly: boolean, studySearchStrategyId: number): Promise<TreeNode[]> {
    this.logger.info(this.constructor.name, 'getAccountTypes', `Start`);
    return firstValueFrom(this.getAccountTypes$(readOnly, studySearchStrategyId));
  }

  public getAccountTypes$(readOnly: boolean, studySearchStrategyId: number): Observable<TreeNode[]> {
    return this.lookupService
      .GetAccountType$(studySearchStrategyId)
      .pipe(map((accounTypes) => this.convertDataToTreeNode(accounTypes, this.getParentId(accounTypes), readOnly))) as Observable<TreeNode[]>;
  }

  public getNumberOfEmployeeAbsoluteYears(readOnly: boolean, startYear: number, endYear: number): TreeNode[] {
    const children: any[] = [];
    for (let i = endYear; i >= startYear; i -= 1) {
      children.push({
        label: i,
        data: i,
        selectable: !readOnly,
      });
    }
    return children;
  }

  public getNumberOfEmployeeRelativeYears(readOnly): TreeNode[] {
    return [
      {
        label: 'Last available year',
        data: NumberOfEmployeesRelativeYears.LastAvailable,
        selectable: !readOnly,
      },
      {
        label: 'Last available year - 1',
        data: NumberOfEmployeesRelativeYears.LastAvailable1,
        selectable: !readOnly,
      },
      {
        label: 'Last available year - 2',
        data: NumberOfEmployeesRelativeYears.LastAvailable2,
        selectable: !readOnly,
      },
      {
        label: 'Last available year - 3',
        data: NumberOfEmployeesRelativeYears.LastAvailable3,
        selectable: !readOnly,
      },
      {
        label: 'Last available year - 4',
        data: NumberOfEmployeesRelativeYears.LastAvailable4,
        selectable: !readOnly,
      },
      {
        label: 'Last available year - 5',
        data: NumberOfEmployeesRelativeYears.LastAvailable5,
        selectable: !readOnly,
      },
      {
        label: 'Last available year - 6',
        data: NumberOfEmployeesRelativeYears.LastAvailable6,
        selectable: !readOnly,
      },
      {
        label: 'Last available year - 7',
        data: NumberOfEmployeesRelativeYears.LastAvailable7,
        selectable: !readOnly,
      },
      {
        label: 'Last available year - 8',
        data: NumberOfEmployeesRelativeYears.LastAvailable8,
        selectable: !readOnly,
      },
      {
        label: 'Last available year - 9',
        data: NumberOfEmployeesRelativeYears.LastAvailable9,
        selectable: !readOnly,
      },
    ];
  }

  transformBvDIndependence(valueLookup: Lookup[]) {
    return [
      {
        data: {
          name: 'A+',
          heading: 'No shareholder with more than 25% of direct or total ownership (="Independent companies")',
          rows: 3,
          value: valueLookup['A+'],
        },
      },
      {
        data: {
          name: 'A',
          value: valueLookup['A'],
        },
      },
      {
        data: {
          name: 'A-',
          value: valueLookup['A-'],
        },
      },
      {
        data: {
          /* tslint:disable */
          name: 'B+',
          heading:
            'No shareholder recorded with more than 50% of direct, indirect or total ownership<br/>One or more shareholders recorded with more than 25% of direct or total ownership',
          rows: 3,
          value: valueLookup['B+'],
          /* tslint:enable */
        },
      },
      {
        data: {
          name: 'B',
          value: valueLookup['B'],
        },
      },
      {
        data: {
          name: 'B-',
          value: valueLookup['B-'],
        },
      },
      {
        data: {
          name: 'C+',
          heading:
            'No shareholder recorded with more than 50% of direct ownership<br/>One shareholder recorded with more than 50% of total ownership (= indirectly majority owned)',
          rows: 2,
          value: valueLookup['C+'],
        },
      },
      {
        data: {
          name: 'C',
          value: valueLookup['C'],
        },
      },
      {
        data: {
          name: 'D',
          heading: 'One shareholder recorded with more than 50% of direct ownership (= directly majority owned)',
          rows: 1,
          value: valueLookup['D'],
        },
      },
      {
        data: {
          name: 'U',
          heading: 'Unknown',
          rows: 1,
          value: valueLookup['U'],
        },
      },
      {
        data: {
          name: 'Add all publicly listed companies',
          columns: 2,
          value: valueLookup['Add all publicly listed Companies'],
        },
      },
      {
        data: {
          /* tslint:disable */
          name: 'Add companies for which all shareholders belong to categories "one or more individuals or families" or "Employees/Managers/Directors" as well as companies for which all shareholders with a stake greater than 25% belong to categories "one or more individuals or families" or "Employees/Managers/Directors"',
          columns: 2,
          value: valueLookup['Add companies for which all shareholders belong to categories...'],
          /* tslint:enable */
        },
      },
      {
        data: {
          name: 'Add branches',
          columns: 2,
          value: valueLookup['Add branches'],
        },
      },
    ];
  }

  public getBvdIndependence$(): Observable<any> {
    return this.lookupService.GetBvdIndependenceIndicator$().pipe(
      map((bvdIndicators) => {
        return bvdIndicators.reduce((mapper, obj) => {
          mapper[obj.displayText] = obj.value;
          return mapper;
        }, {});
      }),
      map(this.transformBvDIndependence)
    );
  }

  public async getShareholderCharacteristics(readOnly: boolean): Promise<TreeNode[]> {
    this.logger.info(this.constructor.name, 'getShareholderCharacteristics', `Start`);
    return firstValueFrom(this.getShareholderCharacteristics$(readOnly));
  }

  public getShareholderCharacteristics$(readOnly: boolean): Observable<TreeNode[]> {
    return this.lookupService
      .GetShareholderCharacteristics$()
      .pipe(map((characteristics) => this.convertDataToTreeNode(characteristics, this.getParentId(characteristics), readOnly))) as Observable<
      TreeNode[]
    >;
  }

  public async getSubsidiaryCharacteristics(readOnly: boolean): Promise<TreeNode[]> {
    this.logger.info(this.constructor.name, 'getShareholderCharacteristics', `Start`);
    return firstValueFrom(this.getSubsidiaryCharacteristics$(readOnly));
  }

  public getSubsidiaryCharacteristics$(readOnly: boolean): Observable<TreeNode[]> {
    return this.lookupService
      .GetSubsidiaryCharacteristics$()
      .pipe(map((characteristics) => this.convertDataToTreeNode(characteristics, this.getParentId(characteristics), readOnly))) as Observable<
      TreeNode[]
    >;
  }
  public getCompustatStatuses$(readOnly: boolean): Observable<TreeNode[]> {
    return this.lookupService
      .GetLookupValues$(LookupType[LookupType.CompustatCompanyStatus])
      .pipe(map((characteristics) => this.convertDataToTreeNode(characteristics, this.getParentId(characteristics), readOnly)));
  }

  private convertDataToTreeNode(data: Lookup[], parentId = 0, readOnly = false): TreeNode[] {
    // const parentLookup = data.find(a => a.lookupId === parentId);
    if (_.isEmpty(data)) {
      return null;
    }

    const options = data.filter((a) => a.parentId === parentId);
    let results: TreeNode[];
    if (options && options.length > 0) {
      results = options.map((option) => this.convertLookupToTreeNode(data, option, readOnly));
    }

    return results;
  }

  private convertLookupToTreeNode(data: Lookup[], lookup: Lookup, readOnly: boolean): TreeNode {
    const treeNode: TreeNode = {
      label: lookup.displayText,
      data: lookup.value,
      selectable: !readOnly,
    };
    treeNode['displayOrder'] = lookup.displayOrder;
    const children = data.filter((a) => a.parentId === lookup.lookupId);
    treeNode.leaf = children && children.length > 0 ? false : true;
    if (children && children.length) {
      treeNode.children = this.convertDataToTreeNode(data, lookup.lookupId, readOnly);
    }
    return treeNode;
  }

  private getParentId(data: Lookup[]): number {
    if (_.isEmpty(data)) {
      return null;
    }

    const parentIds: number[] = _.uniq(data.map((d) => d.parentId));
    const lookupIds: number[] = _.uniq(data.map((d) => d.lookupId));
    const diff = parentIds.filter((pId) => lookupIds.indexOf(pId) < 0);
    return diff && diff.length > 0 ? diff[0] : null;
  }
}
